/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { useTranslations } from "@i18n/next";
import { HallowAnalyticsEvent, useAnalytics } from "@packages/analytics";
import type { AdminDashUser, CommunityType, User } from "@packages/sdk";
import { AccountType, RoleID, useActiveSidebarTab } from "@packages/sdk";
import type { ColumnConfig, TableProps } from "@packages/ui";
import {
  ArrowDownIcon,
  ArrowUpIcon,
  Avatar,
  Button,
  Checkbox,
  cssMediaQueryMinWidthTablet,
  IconButton,
  InfoIcon,
  lineHeights,
  Modal,
  ModalHeader,
  ScrollArea,
  Skeleton,
  SortIcon,
  spacing,
  Table,
  Text,
  useSearchParamState,
} from "@packages/ui";
import { Close as RadixClose } from "@radix-ui/react-dialog";
import { useParams } from "next/navigation";
import { Fragment, useEffect, useMemo } from "react";

import { getBasePADUrl, padTabsPaths } from "../../../../lib";
import { SubscriptionText } from "../../SubscriptionText";
import { EditMemberDropdownMenu } from "../EditMemberDropdownMenu";
import { MemberModalView } from "../MemberModalView";

const checkboxStyles: Interpolation = {
  height: 18,
  marginRight: spacing.s,
  width: 18,
};

const sortableHeaderStyles: Interpolation = {
  alignItems: "center",
  cursor: "pointer",
  display: "flex",
  gap: spacing.xs,
  userSelect: "none",
};

const skeletonStyles: Interpolation = {
  height: lineHeights.bodyS,
};

const tableStyles: Interpolation = {
  marginBottom: spacing.xxxl,
};

const scrollAreaStyles: Interpolation = {
  width: spacing.full,
};

const accountTypeHeader: Interpolation = {
  display: "flex",
  justifyContent: "flex-start",
  alignItems: "center",
  gap: spacing.xs,
};
const accountInfoModal: Interpolation = {
  maxWidth: 400,
  padding: spacing.xl,
  alignItems: "center",
  display: "flex",
  flexDirection: "column",
  gap: spacing.l,
  justifyContent: "flex-start",
  [cssMediaQueryMinWidthTablet]: {
    padding: spacing.xl,
  },
};
const accountInfoHeader: Interpolation = {
  justifyContent: "flex-start",
  margin: spacing.none,
  padding: spacing.none,
  width: spacing.full,
  "& h2": {
    marginBottom: spacing.negativeM,
    justifyContent: "flex-start",
    textAlign: "left",
    width: spacing.full,
  },
};

export type MemberWithAdminFlag = User & {
  is_only_admin?: boolean;
};

type SortState = {
  id: "email" | "first_name" | "last_name";
  desc: boolean;
} | null;

type ParishAdminDashboardMembersProps = Partial<TableProps> & {
  communityId: number;
  communityType: CommunityType;
  loading?: boolean;
  loadingSkeletonsTotal?: number;
  members: MemberWithAdminFlag[];
  baseMemberLinkUrl: string;
  onSortChange?: (columnId: "email" | "first_name" | "last_name") => void;
  sorting?: SortState;
  select?: {
    allIds: number[];
    selectedIds: number[];
    setSelectedIds: (ids: number[]) => void;
  };
  tableCSS?: Interpolation;
};

export const ParishAdminDashboardMembers = ({
  communityId,
  communityType,
  loading = false,
  loadingSkeletonsTotal = 3,
  members,
  baseMemberLinkUrl,
  onSortChange,
  sorting,
  select,
  tableCSS,
  ...props
}: ParishAdminDashboardMembersProps) => {
  const analytics = useAnalytics();
  const { communityId: padActiveCommunityId } = useParams<{
    communityId: string;
  }>();
  const [memberId, setMemberId] = useSearchParamState({
    defaultValue: "",
    key: "memberId",
  });
  const t = useTranslations();

  const activeSidebarTab = useActiveSidebarTab({
    baseUrl: getBasePADUrl({ communityId: parseInt(padActiveCommunityId) }),
    tabs: padTabsPaths,
  });

  const sortableLabel = (
    label: string,
    columnId: "first_name" | "last_name",
  ) => {
    if (!onSortChange) return label;

    return (
      <div
        css={sortableHeaderStyles}
        onClick={() => onSortChange(columnId)}
        role="button"
        tabIndex={0}
      >
        {label}
        {sorting?.id === columnId ? (
          sorting.desc ? (
            <ArrowDownIcon size={18} style={{ cursor: "pointer" }} />
          ) : (
            <ArrowUpIcon size={18} style={{ cursor: "pointer" }} />
          )
        ) : (
          <SortIcon size={18} style={{ cursor: "pointer" }} />
        )}
      </div>
    );
  };

  const columnConfig: {
    [key in keyof (AdminDashUser & {
      is_only_admin?: boolean;
    })]?: ColumnConfig<MemberWithAdminFlag>;
  } = useMemo(() => {
    if (communityType === "school") {
      return {
        select: {
          label: (
            <Checkbox
              checked={
                select?.allIds.length > 0 &&
                select?.allIds.length === select?.selectedIds.length
              }
              name="selectAll"
              onCheckedChange={() =>
                select?.allIds.length === select?.selectedIds.length
                  ? select?.setSelectedIds([])
                  : select?.setSelectedIds(select?.allIds)
              }
              css={checkboxStyles}
            />
          ),
          render: (_, row) => (
            <Checkbox
              checked={select?.selectedIds.includes(row.id)}
              name="select"
              onCheckedChange={() =>
                select?.selectedIds.includes(row.id)
                  ? select?.setSelectedIds(
                      select?.selectedIds.filter((id) => id !== row.id),
                    )
                  : select?.setSelectedIds([...select?.selectedIds, row.id])
              }
              onClick={(e) => e.stopPropagation()}
              css={checkboxStyles}
            />
          ),
          width: 0,
        },
        image_url: {
          label: t("profile_tab_title_default"),
          order: 1,
          render: (val: string) => <Avatar imgSrc={val} size={48} />,
          width: 0,
        },
        last_name: {
          label: sortableLabel(t("admin_dash_last_name"), "last_name"),
          order: 2,
          width: "16%",
        },
        name: {
          label: sortableLabel(t("admin_dash_first_name"), "first_name"),
          order: 3,
          width: "20%",
        },
        login: {
          label: t("login_title"),
          order: 4,
          width: "16%",
        },
        account_type: {
          label: (
            <div css={accountTypeHeader}>
              {t("web_admin_dash_account_type")}
              <Modal
                trigger={
                  <IconButton
                    icon={<InfoIcon size={16} />}
                    variant="transparentNeutral"
                    size="s"
                  />
                }
                contentCSS={accountInfoModal}
              >
                <ModalHeader css={accountInfoHeader}>
                  {t("web_admin_dash_account_types_info_header")}
                </ModalHeader>
                <Text>{t("web_admin_dash_account_types_info_text")}</Text>
                <RadixClose asChild>
                  <Button>{t("web_admin_dash_account_types_info_cta")}</Button>
                </RadixClose>
              </Modal>
            </div>
          ),
          order: 5,
          render: (val) =>
            val === AccountType.Kid
              ? t("web_admin_dash_account_type_kids")
              : t("web_admin_dash_account_type_standard"),
          width: "16%",
        },
        role_id: {
          label: t("admin_dash_role"),
          order: 6,
          width: "16%",
          render: (val: number) =>
            val === RoleID.Student
              ? t("admin_dash_role_student")
              : t("admin_dash_role_teacher"),
        },
        status: {
          label: t("general_word_subscription"),
          order: 7,
          render: (_, row) => (
            <SubscriptionText
              subscription_end_date={row.subscription_end_date as string}
            />
          ),
          width: "16%",
        },
        more_menu: {
          label: "",
          order: 8,
          render: (_, row) => (
            <EditMemberDropdownMenu
              communityId={communityId}
              user={row}
              communityType={communityType}
            />
          ),
          width: 0,
        },
      };
    }
    return {
      image_url: {
        label: t("profile_tab_title_default"),
        order: 0,
        render: (val: string) => <Avatar imgSrc={val} size={48} />,
        width: 0,
      },
      name: { label: t("admin_dash_first_name"), order: 1, width: "20%" },
      last_name: { label: t("admin_dash_last_name"), order: 2, width: "20%" },
      bio: { label: t("profile_bio"), order: 3, width: "20%" },
      is_admin: {
        label: t("group_role_admin"),
        order: 4,
        render: (val: boolean) =>
          val ? t("general_word_yes") : t("general_word_no"),
        width: "20%",
      },
      more_menu: {
        label: "",
        order: 5,
        render: (_, row) => {
          return (
            <EditMemberDropdownMenu
              communityId={communityId}
              user={row}
              communityType={communityType}
            />
          );
        },
        width: 0,
      },
    };
  }, [communityId, communityType, onSortChange, select, sorting, t]);

  const handleRowClick = (row: Partial<MemberWithAdminFlag>) => {
    analytics.track({
      event: HallowAnalyticsEvent.TappedCommunityMemberProfile,
      properties: {
        location: activeSidebarTab ? `${activeSidebarTab}_tab` : "",
      },
    });
    setMemberId(row.id.toString());
  };

  const skeleton = (column: keyof typeof columnConfig) => {
    if (column === "image_url") {
      return <Avatar size={48} loading />;
    }
    return <Skeleton css={skeletonStyles} />;
  };

  useEffect(() => {
    if (memberId)
      analytics.track({
        event: HallowAnalyticsEvent.TappedEditMember,
        properties: {
          member_id: memberId,
          screen_name: "members",
        },
      });
  }, [memberId]);

  const isOnlyAdmin = useMemo(() => {
    return members.find((m) => m.id === parseInt(memberId))?.is_only_admin;
  }, [members, memberId]);

  return (
    <Fragment>
      {memberId && (
        <MemberModalView
          communityId={communityId}
          memberId={parseInt(memberId)}
          isOnlyAdmin={isOnlyAdmin}
          // we can't use null because it's a searchParam state, so it needs to be a string
          onClose={() => setMemberId("")}
        />
      )}
      <ScrollArea
        horizontal
        viewportCSS={[scrollAreaStyles]}
        masksSize="xs"
        masksColor="onOverlayBackground"
      >
        <Table
          columns={columnConfig}
          data={members}
          skeletonRows={loadingSkeletonsTotal}
          onRowClick={handleRowClick}
          loadingSkeleton={skeleton}
          css={[tableStyles, tableCSS]}
          {...props}
        />
      </ScrollArea>
    </Fragment>
  );
};
